import React, { useState, useRef, useEffect, useMemo } from "react";
import { useSound } from "../../utils/sound/SoundProvider";
import useGameBase from '../../hooks/useGameBase';
import SpectatorPerspectiveSelector from "./SpectatorPerspectiveSelector";

function HangmanGame({
  gameState,
  gamePlayers = [],
  user = {},
  onGameAction,
  isSpectator = false,
  onPlayAgain,
  socket,
  roomId,
}) {
  // --- Spectator Perspective logic (Canasta/Bingo style) ---
  const perspectiveOptions = useMemo(() => {
    if (Array.isArray(gamePlayers) && gamePlayers.length > 0) return gamePlayers;
    if (Array.isArray(gameState?.players) && gameState.players.length > 0) return gameState.players;
    return [];
  }, [gamePlayers, gameState?.players]);

  const [spectatorViewPlayerId, setSpectatorViewPlayerId] = useState(null);
  const perspectivePlayer = useMemo(() => {
    if (!isSpectator || !spectatorViewPlayerId) return null;
    return perspectiveOptions.find((player) => {
      const playerId = player?.userId || player?.socketId || player?.id || player?.username;
      return String(playerId) === String(spectatorViewPlayerId);
    }) || null;
  }, [perspectiveOptions, isSpectator, spectatorViewPlayerId]);

  useEffect(() => {
    if (!isSpectator) {
      setSpectatorViewPlayerId(null);
      return;
    }
    if (!Array.isArray(perspectiveOptions) || perspectiveOptions.length === 0) {
      setSpectatorViewPlayerId(null);
      return;
    }
    const exists = perspectiveOptions.some((player) => {
      const playerId = player?.userId || player?.socketId || player?.id || player?.username;
      return String(playerId) === String(spectatorViewPlayerId);
    });
    if (!exists) {
      const first = perspectiveOptions[0];
      setSpectatorViewPlayerId(first?.userId || first?.socketId || first?.id || first?.username || null);
    }
  }, [perspectiveOptions, isSpectator, spectatorViewPlayerId]);

  const { submitAction, findPlayerById, normalizedState, perspectiveIds } = useGameBase({ gameState, gamePlayers, user, socket, roomId, perspectivePlayer });


  const [guess, setGuess] = useState("");
  const [error, setError] = useState("");
  const guessInputRef = useRef(null);

  useEffect(() => {
    if (!gameState.finished && guessInputRef.current)
      guessInputRef.current.focus();
  }, [gameState.finished]);

  const isFinished = !!gameState.finished;
  const sound = useSound();
  const prevWrongCountRef = useRef(0);
  const prevGuessedRef = useRef([]);
  // Use normalized state and tolerant player lookup from useGameBase
  const state = normalizedState || gameState || {};
  // Perspective-aware 'me' lookup (Canasta style)
  const me = useMemo(() => {
    // Try to find the player matching the current perspective
    if (Array.isArray(state.players)) {
      for (const key of perspectiveIds) {
        const p = state.players.find(
          (pl) =>
            (pl.socketId && String(pl.socketId) === String(key)) ||
            (pl.userId && String(pl.userId) === String(key)) ||
            (pl.id && String(pl.id) === String(key)) ||
            (pl.username && String(pl.username) === String(key))
        );
        if (p) return p;
      }
    }
    // Fallback to the gamePlayers prop lookup
    for (const key of perspectiveIds) {
      const p = findPlayerById(key);
      if (p) return p;
    }
    return null;
  }, [state.players, perspectiveIds, findPlayerById]);

  // Build an array of characters with reveal flags. Reveal when the
  // current player has guessed the letter or when the game has finished.
  const word = (state.word || "") + "";
  const wordChars = word.split("").map((c, i) => {
    const revealedByMe = me && Array.isArray(me.guessedLetters) && me.guessedLetters.includes(c);
    return { char: c, revealedByMe, index: i };
  });

  // track which indices have been flipped visually (for immediate flips)
  const [flippedIndices, setFlippedIndices] = useState(() => new Set());
  // initialize flippedIndices for letters already guessed by the perspective player
  useEffect(() => {
    try {
      const s = new Set();
      if (Array.isArray(wordChars) && wordChars.length && me && Array.isArray(me.guessedLetters)) {
        wordChars.forEach((wc) => {
          if (me.guessedLetters.includes(wc.char)) s.add(wc.index);
        });
      }
      setFlippedIndices(s);
    } catch (e) {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(wordChars.map(w=>w.char)), me && JSON.stringify(me && me.guessedLetters)]);

  // When the game finishes, mark prevFinished so stagger runs only once per finish
  const prevFinishedRef = React.useRef(false);
  useEffect(() => {
    if (!state.finished || prevFinishedRef.current) return;
    prevFinishedRef.current = true;
  }, [state.finished]);

  // --- Spectator Perspective Selector UI ---
  const showSpectatorPerspectiveSelector = isSpectator && perspectiveOptions.length > 0;

  // Simple SVG hangman renderer using wrongCount to reveal parts
  const HangmanVisual = ({
    wrongCount,
    maxWrong,
    width = 160,
    height = 200,
    eliminated = false,
  }) => {
    const parts = Math.min(maxWrong, wrongCount);
    // parts order: head, body, leftArm, rightArm, leftLeg, rightLeg, leftFoot, rightFoot, leftHand, rightHand
    const colorClass = eliminated
      ? "text-red-600 dark:text-red-400"
      : "text-gray-700 dark:text-gray-300";

    // Track increases to wrongCount and flash all parts briefly when any new wrong occurs
    const prevWrongRef = useRef(wrongCount || 0);
    const [flashAll, setFlashAll] = useState(false);

    useEffect(() => {
      const prev = prevWrongRef.current || 0;
      if (wrongCount > prev) {
        // trigger flash for all parts
        setFlashAll(true);
        prevWrongRef.current = wrongCount;
        const t = setTimeout(() => setFlashAll(false), 700);
        return () => clearTimeout(t);
      }
      prevWrongRef.current = wrongCount;
      return undefined;
    }, [wrongCount, parts]);

    return (
      <>
        <style>{`
          @keyframes hangmanFlash {
            0% { stroke: #ef4444; stroke-opacity: 1; }
            50% { stroke-opacity: 0.4; }
            100% { stroke: currentColor; stroke-opacity: 1; }
          }
          .hangman-flash { animation: hangmanFlash 900ms ease; stroke: #ef4444 !important; }
        `}</style>
        <svg
          width={width}
          height={height}
          viewBox="0 0 160 200"
          className={`bg-transparent ${colorClass}`}
        >
          {/* gallows */}
          <line
            x1="10"
            y1="180"
            x2="150"
            y2="180"
            stroke="currentColor"
            strokeWidth="3"
            className="opacity-90"
          />
          <line
            x1="40"
            y1="180"
            x2="40"
            y2="20"
            stroke="currentColor"
            strokeWidth="3"
            className="opacity-90"
          />
          <line
            x1="40"
            y1="20"
            x2="100"
            y2="20"
            stroke="currentColor"
            strokeWidth="3"
            className="opacity-90"
          />
          <line
            x1="100"
            y1="20"
            x2="100"
            y2="40"
            stroke="currentColor"
            strokeWidth="3"
            className="opacity-90"
          />

          {/* head */}
          {parts >= 1 && (
            <circle
              cx="100"
              cy="55"
              r="15"
              stroke={flashAll ? "#ef4444" : "currentColor"}
              strokeWidth={flashAll ? 6 : 3}
              fill="none"
              style={{
                transition: "stroke 400ms ease, stroke-width 400ms ease",
              }}
            />
          )}
          {/* body */}
          {parts >= 2 && (
            <line
              x1="100"
              y1="70"
              x2="100"
              y2="115"
              stroke={flashAll ? "#ef4444" : "currentColor"}
              strokeWidth={flashAll ? 6 : 3}
              style={{
                transition: "stroke 400ms ease, stroke-width 400ms ease",
              }}
            />
          )}
          {/* left arm */}
          {parts >= 3 && (
            <line
              x1="100"
              y1="85"
              x2="80"
              y2="100"
              stroke={flashAll ? "#ef4444" : "currentColor"}
              strokeWidth={flashAll ? 6 : 3}
              style={{
                transition: "stroke 400ms ease, stroke-width 400ms ease",
              }}
            />
          )}
          {/* right arm */}
          {parts >= 4 && (
            <line
              x1="100"
              y1="85"
              x2="120"
              y2="100"
              stroke={flashAll ? "#ef4444" : "currentColor"}
              strokeWidth={flashAll ? 6 : 3}
              style={{
                transition: "stroke 400ms ease, stroke-width 400ms ease",
              }}
            />
          )}
          {/* left leg */}
          {parts >= 5 && (
            <line
              x1="100"
              y1="115"
              x2="85"
              y2="145"
              stroke={flashAll ? "#ef4444" : "currentColor"}
              strokeWidth={flashAll ? 6 : 3}
              style={{
                transition: "stroke 400ms ease, stroke-width 400ms ease",
              }}
            />
          )}
          {/* right leg */}
          {parts >= 6 && (
            <line
              x1="100"
              y1="115"
              x2="115"
              y2="145"
              stroke={flashAll ? "#ef4444" : "currentColor"}
              strokeWidth={flashAll ? 6 : 3}
              style={{
                transition: "stroke 400ms ease, stroke-width 400ms ease",
              }}
            />
          )}

          {/* left foot (7) */}
          {parts >= 7 && (
            <line
              x1="85"
              y1="145"
              x2="78"
              y2="150"
              stroke={flashAll ? "#ef4444" : "currentColor"}
              strokeWidth={flashAll ? 6 : 3}
              style={{
                transition: "stroke 400ms ease, stroke-width 400ms ease",
              }}
            />
          )}
          {/* right foot (8) */}
          {parts >= 8 && (
            <line
              x1="115"
              y1="145"
              x2="122"
              y2="150"
              stroke={flashAll ? "#ef4444" : "currentColor"}
              strokeWidth={flashAll ? 6 : 3}
              style={{
                transition: "stroke 400ms ease, stroke-width 400ms ease",
              }}
            />
          )}

          {/* left hand (9) */}
          {parts >= 9 && (
            <line
              x1="80"
              y1="100"
              x2="74"
              y2="94"
              stroke={flashAll ? "#ef4444" : "currentColor"}
              strokeWidth={flashAll ? 6 : 3}
              style={{
                transition: "stroke 400ms ease, stroke-width 400ms ease",
              }}
            />
          )}
          {/* right hand (10) */}
          {parts >= 10 && (
            <line
              x1="120"
              y1="100"
              x2="126"
              y2="94"
              stroke={flashAll ? "#ef4444" : "currentColor"}
              strokeWidth={flashAll ? 6 : 3}
              style={{
                transition: "stroke 400ms ease, stroke-width 400ms ease",
              }}
            />
          )}
        </svg>
      </>
    );
  };

  const submitGuess = (val) => {
    if (!val) {
      setError("Enter a single letter or a whole word");
      return;
    }
    // single letter
    if (val.length === 1) {
      if (!/^[a-zA-Z]$/.test(val)) {
        setError("Enter a single letter (A-Z)");
        return;
      }
      if (me && me.eliminated) {
        setError("You have been eliminated. Wait for others to finish.");
        return;
      }
  submitAction({ type: "guess", guess: val.toLowerCase() }).catch(()=>{});
      setGuess("");
      return;
    }
    // multi-letter: must match length
  const targetLen = (state.word || "").length;
    if (val.length === targetLen) {
      if (me && me.eliminated) {
        setError("You have been eliminated.");
        return;
      }
  submitAction({ type: "guess", guess: val.toLowerCase() }).catch(()=>{});
      setGuess("");
      return;
    }
    setError(
      "Enter either a single letter, or a whole word of the correct length"
    );
  };

  // Play sounds on state changes: wrong guess increments and finished state
  useEffect(() => {
    try {
      if (!sound || !sound.enabled || !sound.enabled()) return;
      if (state.finished) {
        // nothing else required here; sound handling below uses state.winnerId
        if (state.winnerId) {
          if (state.winnerId === user?.userId) sound.play("hangman:win");
          else sound.play("hangman:lose");
        } else if (state.won) {
          sound.play("hangman:win");
        }
      }
    } catch (e) {
      // ignore
    }
  }, [state.finished, state.winnerId, state.won, sound, user?.userId, state.players]);

  // Play an immediate positive sound when the server signals a correct whole-word guess
  // via `showWinningState`. The server may use this flag before setting `finished`.
  useEffect(() => {
    try {
      if (!sound || !sound.enabled || !sound.enabled()) return;
      if (state.showWinningState) {
        // Play a short positive cue immediately; final win/lose sounds are handled
        // by the finished effect above.
        sound.play("ui:positive");
      }
    } catch (e) {}
  }, [state.showWinningState, sound]);

  // Play wrong-guess sound when current player's wrongCount increases
  useEffect(() => {
    try {
      if (!sound || !sound.enabled || !sound.enabled()) return;
      // Prefer authoritative state.players for per-player fields (wrongCount), fallback to props
      const mePlayer = Array.isArray(state.players)
        ? state.players.find(
            (pl) => (user && user.userId && pl.userId === user.userId) || (socket && socket.id && pl.socketId === socket.id)
          ) || (findPlayerById(user?.userId) || findPlayerById(socket?.id))
        : findPlayerById(user?.userId) || findPlayerById(socket?.id);
      const prev = prevWrongCountRef.current || 0;
      const now = mePlayer ? mePlayer.wrongCount || 0 : 0;
      if (now > prev) {
        sound.play("ui:negative");
      }
      prevWrongCountRef.current = now;
    } catch (e) {
      // ignore
    }
  }, [state.players, sound, user?.userId, findPlayerById, socket?.id, socket, user]);

  // Play positive/negative sound when a new guessed letter for current player is recorded
  useEffect(() => {
    try {
      if (!sound || !sound.enabled || !sound.enabled()) return;
      const mePlayer = Array.isArray(state.players)
        ? state.players.find(
            (pl) => (user && user.userId && pl.userId === user.userId) || (socket && socket.id && pl.socketId === socket.id)
          ) || (findPlayerById(user?.userId) || findPlayerById(socket?.id))
        : findPlayerById(user?.userId) || findPlayerById(socket?.id);
      const prevGuessed = prevGuessedRef.current || [];
      const nowGuessed =
        mePlayer && Array.isArray(mePlayer.guessedLetters)
          ? mePlayer.guessedLetters
          : [];
      // determine newly added letters
      const added = nowGuessed.filter((l) => !prevGuessed.includes(l));
      if (added && added.length) {
  const word = (state.word || "").toLowerCase();
        added.forEach((letter) => {
          if (word.includes(letter)) {
            sound.play("ui:positive");
          } else {
            // incorrect letter sound is handled by wrongCount effect, but play here as well for immediacy
            sound.play("ui:negative");
          }
        });
      }
      prevGuessedRef.current = nowGuessed.slice ? nowGuessed.slice() : nowGuessed;
    } catch (e) {
      // ignore
    }
  }, [state.players, state.word, sound, user?.userId, findPlayerById, socket?.id, socket, user]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");
    submitGuess((guess || "").trim());
  };

  const handleAlphabetTileClick = (letter) => {
    if (isFinished || isSpectator || (me && me.eliminated)) return;
    setError("");
    setGuess(letter);
    submitGuess(letter);
  };

  // per-player guess summaries (use explicit per-player arrays)
  const myGuessed = me && Array.isArray(me.guessedLetters) ? me.guessedLetters : [];
  const myCorrect = myGuessed.filter((l) => (state.word || "").includes(l));
  const myWrong = me && Array.isArray(me.wrongLetters) ? me.wrongLetters : [];
  const correctLetterSet = new Set(myCorrect.map((letter) => String(letter).toUpperCase()));
  const wrongLetterSet = new Set(myWrong.map((letter) => String(letter).toUpperCase()));
  const alphabetLetters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
  const alphabetRows = useMemo(() => [
    alphabetLetters.slice(0, 13),
    alphabetLetters.slice(13, 26),
  ], [alphabetLetters]);

  // Resolve winner's display name when present
  const winnerName = (() => {
    const wid = state && state.winnerId;
    if (!wid) return null;
    // Prefer authoritative state.players, fallback to gamePlayers prop
    const fromState = Array.isArray(state.players)
      ? state.players.find((p) => p.userId === wid || p.id === wid || p.socketId === wid)
      : null;
    if (fromState) return fromState.username || fromState.id || wid;
    const fromProps = Array.isArray(gamePlayers)
      ? gamePlayers.find((p) => p.userId === wid || p.id === wid || p.socketId === wid)
      : null;
    if (fromProps) return fromProps.username || fromProps.id || wid;
    return wid;
  })();

  // Word to reveal when the game ends, prefer originalWord if present
  const revealedWord = state && (state.originalWord || state.word) ? String(state.originalWord || state.word) : null;

  // Display name of the chosen hangman set (from server config)
  const setName = (gameState && gameState.set) || (gameState && gameState.config && gameState.config.set) || null;
  const setLabel = setName
    ? String(setName).replace(/[_-]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : null;

  return (
    <div className="mt-4 bg-white dark:bg-gray-800 rounded shadow p-4 text-gray-900 dark:text-gray-100">
      {showSpectatorPerspectiveSelector && (
        <SpectatorPerspectiveSelector
          players={perspectiveOptions}
          value={spectatorViewPlayerId || ""}
          onChange={setSpectatorViewPlayerId}
        />
      )}
      {isFinished && (
        <div className="result mt-3 text-lg">
          {gameState.winnerId
            ? gameState.winnerId === user?.userId
              ? "You win!"
              : `${winnerName} won!`
            : gameState.isFinished
            ? revealedWord
              ? `Word guessed! The answer was: ${revealedWord}`
              : "Word guessed!"
            : revealedWord
            ? `Game over! The answer was: ${revealedWord}`
            : "Game over!"}
        </div>
      )}

      {/* Hangman visual for current player */}
      <div className="mt-6 w-full">
        <div className="flex flex-col items-center">
          {/* Large hangman for current player */}
          <HangmanVisual
            wrongCount={(me && me.wrongCount) || 0}
            maxWrong={gameState.maxWrong || 6}
            eliminated={me && me.eliminated}
          />
          {/* Center the word being guessed under the main visual */}
          <div className="mt-3 text-center">
            {/* flip-card styles (shared) */}
            <style>{`
              .hangman-flip-outer { perspective: 800px; }
              .hangman-flip-inner { transition: transform 600ms cubic-bezier(.2,.9,.2,1); transform-style: preserve-3d; position: relative; width: 100%; height: 100%; }
              .hangman-flip-inner.flipped { transform: rotateY(180deg); }
              .hangman-flip-face { backface-visibility: hidden; -webkit-backface-visibility: hidden; position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; border-radius: 0.25rem; }
              .hangman-flip-back { transform: rotateY(180deg); }
            `}</style>
            {/* choose tile size classes based on word length so whole word can fit */}
            {(() => {
              const len = (word || "").length || 0;
              // default classes (smaller)
              let tileWH = "w-7 h-9";
              let tileText = "text-sm";
              let spacer = "w-3 h-4";
              // reduce sizes for longer words
              if (len > 10 && len <= 15) {
                tileWH = "w-6 h-8";
                tileText = "text-xs";
                spacer = "w-2 h-3";
              } else if (len > 15 && len <= 22) {
                tileWH = "w-5 h-7";
                tileText = "text-xs";
                spacer = "w-2 h-3";
              } else if (len > 22) {
                tileWH = "w-5 h-6";
                tileText = "text-xs";
                spacer = "w-2 h-2";
              }
              // Group contiguous non-space characters into word chunks so whole words wrap together
              const words = [];
              let current = [];
              wordChars.forEach((wc) => {
                if (wc.char === ' ') {
                  if (current.length) {
                    words.push(current);
                    current = [];
                  }
                  // represent a space as an empty separator between words
                  words.push(null);
                } else {
                  current.push(wc);
                }
              });
              if (current.length) words.push(current);

              return (
                <div className="inline-flex flex-wrap justify-center hangman-flip-outer gap-1 gap-y-2">
                  {words.map((wordArr, wi) => {
                    if (!wordArr) {
                      // render a small gap for spaces between words
                      return <div key={`sp-${wi}`} style={{ width: spacer.replace(/[^0-9]/g, '') ? undefined : undefined }} className={`mx-1`} />;
                    }
                    return (
                      <div key={`w-${wi}`} className="inline-flex flex-nowrap items-center mx-1">
                        {wordArr.map((wc) => {
                          // punctuation that should be shown as-is and not guessable
                          const punct = ['-', "'", '/', ':', '.', ',', '!', '?', '&', '(', ')', '[', ']', '{', '}', ';'];
                          if (punct.includes(wc.char)) {
                            return (
                              <div key={wc.index} className={`mx-0.5 ${tileWH}`} aria-hidden={false}>
                                <div className={`text-gray-700 dark:text-gray-200 ${tileText} flex items-center justify-center`} aria-label={`punctuation ${wc.char}`}>
                                  {String(wc.char)}
                                </div>
                              </div>
                            );
                          }
                          const revealed = !!(state.showWinningState || state.finished || wc.revealedByMe || (flippedIndices && flippedIndices.has && flippedIndices.has(wc.index)));
                          const delayMs = state.showWinningState ? wc.index * 90 : 0;
                          return (
                            <div key={wc.index} className={`mx-0.5 ${tileWH}`}>
                              <div
                                className={"hangman-flip-inner " + (revealed ? "flipped" : "")}
                                aria-hidden={!revealed}
                                style={{ width: '100%', height: '100%', transitionDelay: `${delayMs}ms` }}
                              >
                                <div
                                  className={`hangman-flip-face hangman-flip-front border text-transparent bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 font-mono ${tileText}`}
                                  aria-label={`hidden letter ${wc.index + 1}`}
                                >
                                  &nbsp;
                                </div>
                                <div
                                  className={`hangman-flip-face hangman-flip-back border bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100 border-gray-300 dark:border-gray-600 font-mono ${tileText}`}
                                  aria-label={`letter ${wc.char}`}
                                >
                                  {String(wc.char).toUpperCase()}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>
        <div className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
          {setLabel ? <div><i><strong>{setLabel}</strong></i></div> : null}
        </div>

        <div className="mt-6">
          <div className="text-sm mt-3">
            Guesses left:{" "}
            {Math.max(
              0,
              (gameState.maxWrong || 6) - ((me && me.wrongCount) || 0)
            )}
          </div>
          <div className="mt-2">
            <div className="mb-1 select-none">
              {alphabetRows.map((row, rowIdx) => (
                <div key={rowIdx} className="flex flex-row gap-1 justify-center mb-0.5">
                  {row.map((letter) => {
                    const isCorrect = correctLetterSet.has(letter);
                    const isWrong = wrongLetterSet.has(letter);
                    const isUsed = isCorrect || isWrong;
                    const letterClass = isCorrect
                      ? "bg-green-500 text-black border-green-600"
                      : isWrong
                      ? "bg-red-400 dark:bg-red-700 text-gray-100 border-gray-500 dark:border-gray-600"
                      : "bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 border-gray-300 dark:border-gray-700";

                    return (
                      <button
                        type="button"
                        key={letter}
                        className={`w-5 h-5 flex items-center justify-center rounded font-mono text-base border transition-colors duration-200 ${letterClass} ${isUsed || isFinished || isSpectator || (me && me.eliminated) ? "cursor-default opacity-90" : "cursor-pointer hover:scale-110 hover:border-blue-400"}`}
                        style={{ textTransform: "uppercase", letterSpacing: "0.05em" }}
                        onClick={() => handleAlphabetTileClick(letter)}
                        disabled={isUsed || isFinished || isSpectator || (me && me.eliminated)}
                      >
                        {letter}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

        </div>
        {!isFinished && !isSpectator && (
          <div className="mt-4">
            <form className="flex" onSubmit={handleSubmit}>
              <input
                ref={guessInputRef}
                type="text"
                value={guess}
                onChange={(e) => setGuess(e.target.value)}
                placeholder="Letter or Guess the answer"
                className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 p-2 flex-1 mr-2 focus:outline-none focus:ring-2 focus:ring-blue-300 dark:focus:ring-blue-400"
                disabled={isFinished || (me && me.eliminated)}
              />
              <button
                type="submit"
                className="bg-blue-500 hover:bg-blue-600 text-white p-2 rounded"
                disabled={isFinished || (me && me.eliminated)}
              >
                Submit
              </button>
            </form>
            {error && (
              <div style={{ color: "red", marginTop: "0.5rem" }}>{error}</div>
            )}
          </div>
        )}
        {/* Other players mini grid (4 per row) */}
        <div className="mt-4">
          <div className="grid grid-cols-4 gap-2 items-start">
            {Array.isArray(gameState.players)
              ? gameState.players
                  .filter((p) => p.userId !== user?.userId)
                  .map((p) => {
                    const partsLeft = Math.max(
                      0,
                      (gameState.maxWrong || 6) - (p.wrongCount || 0)
                    );
                    return (
                      <div
                        key={p.userId}
                        className="flex flex-col items-center p-1 text-center text-xs"
                      >
                        <div className="truncate w-full font-medium text-sm">
                          {p.username || p.id}
                        </div>
                        <div className="text-[10px] text-gray-600 dark:text-gray-400">
                          {p.eliminated ? "eliminated" : `${partsLeft} left`}
                        </div>
                        <div className="mt-1">
                          <HangmanVisual
                            wrongCount={p.wrongCount || 0}
                            maxWrong={gameState.maxWrong || 6}
                            width={56}
                            height={72}
                            eliminated={p.eliminated}
                          />
                        </div>
                      </div>
                    );
                  })
              : null}
          </div>
        </div>
      </div>
    </div>
  );
}

HangmanGame.Guide = function HangmanGuide() {
  return (
    <div>
      <h3 className="font-bold mb-2">How to Play Hangman</h3>
      <ul className="list-disc ml-6">
        <li>Guess letters to reveal the hidden word.</li>
        <li>Each wrong guess draws part of the hangman.</li>
        <li>Guess the word before the hangman is complete!</li>
      </ul>
    </div>
  );
};

export default HangmanGame;
